import { Suspense } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function KitchenElectricalPage() {
  useEffect(() => {
    document.title = 'Kitchen Electrical Installations Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional kitchen electrical installations in Watford. Cooker circuits, appliance connections, and kitchen lighting. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/kitchen-electrical",
      "areaServed": "Watford",
      "serviceType": "Kitchen Electrical Installations",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
                <i className="ri-map-pin-line text-amber-500"></i>
                <span className="text-sm font-medium text-gray-700">Serving Watford &amp; Surrounding Areas</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Kitchen Electrical Installations <span className="text-amber-500">in Watford</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Complete electrical solutions for kitchen renovations and new builds. From appliance circuits to under-cabinet lighting, we handle all your kitchen electrical needs throughout Watford and Rickmansworth.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  Call 020 3198 1818
                </a>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  Get Free Quote
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">NICEIC Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">15 Years Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">Full Certification</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Kitchen Electrical Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive electrical installations for modern kitchens
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-restaurant-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Appliance Circuits</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated circuits for ovens, hobs, dishwashers, washing machines, and other kitchen appliances.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-plug-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Socket Installation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Multiple socket outlets positioned for convenience, including USB sockets and pop-up units.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Kitchen Lighting</h3>
                <p className="text-gray-600 leading-relaxed">
                  Ceiling lights, under-cabinet lighting, plinth lights, and feature lighting for ambiance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-fire-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cooker &amp; Hob Wiring</h3>
                <p className="text-gray-600 leading-relaxed">
                  Professional installation of electric cookers, induction hobs, and range cookers with correct cable sizing.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-wind-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Extractor Fans</h3>
                <p className="text-gray-600 leading-relaxed">
                  Cooker hood extraction systems with appropriate ventilation and electrical connections.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-water-flash-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Water Heaters &amp; Taps</h3>
                <p className="text-gray-600 leading-relaxed">
                  Instant hot water taps, under-sink water heaters, and boiling water tap installations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Kitchen Renovation Process */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Kitchen Electrical Installation Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Working seamlessly with your kitchen fitters
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Design Consultation</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Review kitchen plans and advise on optimal socket, lighting, and appliance positions.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">First Fix</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Install cables, back boxes, and circuits before kitchen units are fitted.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Second Fix</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Fit sockets, switches, lights, and connect appliances after units are installed.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Testing &amp; Certification</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Complete electrical testing and provide certification for Building Regulations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Appliance Requirements */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Kitchen Appliance Electrical Requirements</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Proper circuits for safe appliance operation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-fire-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Electric Cooker/Oven</h3>
                    <p className="text-sm text-gray-600">Dedicated 32A or 45A circuit with cooker control unit</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-temp-hot-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Induction Hob</h3>
                    <p className="text-sm text-gray-600">Dedicated 32A circuit, separate from oven if required</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-drop-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Dishwasher</h3>
                    <p className="text-sm text-gray-600">13A fused spur or dedicated socket with isolation switch</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-t-shirt-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Washing Machine</h3>
                    <p className="text-sm text-gray-600">13A socket or fused spur with easy access for isolation</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-fridge-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Fridge/Freezer</h3>
                    <p className="text-sm text-gray-600">Dedicated unswitched socket to prevent accidental disconnection</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-water-flash-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Boiling Water Tap</h3>
                    <p className="text-sm text-gray-600">13A fused spur under sink with RCD protection</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lighting Options */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Kitchen Lighting Solutions</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Create the perfect ambiance with layered lighting
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Task Lighting</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Under-cabinet LED strips</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Worktop spotlights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Cooker hood lighting</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Ambient Lighting</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Ceiling downlights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Pendant lights over island</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Dimmable LED panels</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Feature Lighting</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Plinth LED lighting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Glass cabinet lighting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-500 mt-0.5"></i>
                    <span>Colour-changing strips</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Beyond Electrical?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert kitchen electrical installations in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Work With Fitters</h3>
                <p className="text-gray-600 leading-relaxed">
                  We coordinate seamlessly with kitchen fitters to ensure smooth installation timing.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">15 Years Experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Extensive experience with kitchen electrical installations and renovations.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Fully Certified</h3>
                <p className="text-gray-600 leading-relaxed">
                  All work certified and compliant with Building Regulations Part P.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Areas We Cover</h2>
              <div className="flex flex-wrap justify-center gap-4 text-gray-700">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Watford</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Rickmansworth</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Bushey</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Abbots Langley</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Croxley Green</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Chorleywood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Northwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Radlett</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Borehamwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">St Albans</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Hemel Hempstead</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Harrow</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Planning a Kitchen Renovation?</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get a free quote for your kitchen electrical installation in Watford
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}